import React, { FC, useState } from 'react';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { updateOrderPanNumberTemplate } from 'apollo/templates/order';
import { panNumbersTemplate } from 'apollo/templates/pan-number';
import { Order } from 'models/order';
import { PanNumber } from 'models/pan-number';
import { Box, Button } from '@mui/material';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PanNumberSelect from 'components/shared/pan-number-select/pan-number-select.component';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type ChangePanNumberDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
};

const ChangePanNumberDialog: FC<ChangePanNumberDialogProps> = ({ onUpdate, onClose, open, order }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [selectedPanNumber, setSelectedPanNumber] = useState<PanNumber | undefined>(undefined);
    const [panNumbers, setPanNumbers] = useState<PanNumber[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    useSeedOnOpen(open, () => {
        setSelectedPanNumber(
            order?.panNumber ? { value: order.panNumber, description: order.panNumberDescription } : undefined,
        );
    });

    useQuery(panNumbersTemplate, {
        onCompleted: ({ panNumbers: fetchedPanNumbers }) => {
            setPanNumbers(fetchedPanNumbers);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [updatePanNumber] = useMutation(updateOrderPanNumberTemplate, {
        onCompleted: ({ updateOrderPanNumber: updatedOrder }) => {
            onClose();
            onUpdate?.({ ...order, ...updatedOrder });
        },
        onError(error) {
            showBoundary(error);
            onClose();
        },
    });

    const handleSave = async () => {
        await updatePanNumber({
            variables: {
                orderId: order?._id,
                panNumber: selectedPanNumber?.value,
                panNumberDescription: selectedPanNumber?.description,
            },
        });
    };

    const handleClose = () => {
        setSelectedPanNumber(
            order?.panNumber ? { value: order.panNumber, description: order.panNumberDescription } : undefined,
        );
        onClose();
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={handleClose}
            title={translate('orders:PAN_NUMBER')}
            actions={
                <Button
                    disabled={!selectedPanNumber?.value}
                    variant="contained"
                    onClick={handleSave}
                    data-test="change-pan-number-dialog.save-btn"
                >
                    {translate('common:SAVE')}
                </Button>
            }
        >
            <Box mt={1}>
                <PanNumberSelect
                    panNumbers={panNumbers}
                    selectedPanNumber={selectedPanNumber?.value}
                    onPanNumberChange={setSelectedPanNumber}
                />
            </Box>
        </DHDialog>
    );
};

export default ChangePanNumberDialog;
